import { z } from "zod";

import { env } from "@/common/env/client.mjs";
import { isObject } from "@/common/helpers/lang";
import type { InferModelType } from "@/common/helpers/models";
import { createModel } from "@/common/helpers/models";

export type PriceString = InferModelType<typeof PriceString>;
export const PriceString = createModel(
  z
    .string()
    .regex(/^\d+\.\d{1,2}$/)
    .brand("PriceString"),
  (model) => ({
    fromPrice: (price: Price) => model.from(String(price.amount)),
    toPrice: (price: PriceString) => Price.from(price),
  }),
);

export type PriceNumber = InferModelType<typeof PriceNumber>;
export const PriceNumber = createModel(z.number().min(0).brand("PriceNumber"), (model) => ({
  fromPrice: (price: Price) => model.from(price.amount),
  toPrice: (price: InferModelType<typeof model>) => Price.from(price),
}));

export type Price = InferModelType<typeof priceModel>;
const priceModel = createModel(
  z.object({
    amount: z.number().min(0),
    currency: z.string().min(2),
  }),
);

export type PriceInput = string | number | { amount: number; currency?: string };

export const Price = {
  ...priceModel,
  from: from,
  fromDecimal: (input: PriceInput) => {
    const price = from(input);

    return priceModel.from({ ...price, amount: price.amount * 100 });
  },
  format: ({ amount, currency }: Price, locale: string, digits = 2) =>
    new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: digits }).format(amount / 100),
};

function from(input: PriceInput) {
  const amount = isObject(input) ? input.amount : Number(input);
  const currency = isObject(input) && input.currency ? input.currency : env.NEXT_PUBLIC_CURRENCY;

  return priceModel.from({ amount, currency });
}
